import {
  CAMERA_PREVIEW_MIN_HEIGHT_PX,
  CAMERA_PREVIEW_MIN_WIDTH_PX,
  cameraCaptureLayoutAspectRatio,
  computeCameraPreviewContainerLayout,
  computeFilledCameraPreviewLayout,
  readCameraCaptureDeviceKind,
  shouldFillCameraCaptureViewport,
  type CameraCaptureDeviceKind,
  type CameraCaptureViewport,
} from "./cameraCaptureLayout";

export type CameraCaptureModalLayoutInput = {
  viewport: CameraCaptureViewport;
  pageContentWidth: number;
  previewAspect: number;
  touchCoarse: boolean;
  touchHoverNone: boolean;
  floorTablet: boolean;
  hasHint: boolean;
};

export type CameraCaptureModalLayoutResult = {
  inputsReady: boolean;
  fillViewportPreview: boolean;
  deviceKind: CameraCaptureDeviceKind;
  layoutAspect: number;
  filledLayout: ReturnType<typeof computeFilledCameraPreviewLayout> | null;
  previewContainerLayout: ReturnType<typeof computeCameraPreviewContainerLayout> | null;
  previewWidth: number;
  previewHeight: number;
  cardWidth: number;
  cardMaxHeight: string;
  previewStyle: {
    width: string;
    height: string;
    minWidth: string;
    minHeight: string;
    maxWidth: string;
  };
  stuckAtMinimum: boolean;
};

const MIN_READY_PAGE_WIDTH_PX = 320;
const MIN_READY_VIEWPORT_HEIGHT_PX = 400;

export function isCameraCaptureModalLayoutInputReady(input: {
  pageContentWidth: number;
  viewport: CameraCaptureViewport;
}): boolean {
  return (
    input.pageContentWidth >= MIN_READY_PAGE_WIDTH_PX
    && input.viewport.width >= MIN_READY_PAGE_WIDTH_PX
    && input.viewport.height >= MIN_READY_VIEWPORT_HEIGHT_PX
  );
}

export function isCameraCaptureLayoutStuckAtMinimum(input: {
  pageContentWidth: number;
  viewportHeight: number;
  previewWidth: number;
  previewHeight: number;
}): boolean {
  return (
    input.pageContentWidth >= 400
    && input.viewportHeight >= 600
    && input.previewWidth <= CAMERA_PREVIEW_MIN_WIDTH_PX + 4
    && input.previewHeight <= CAMERA_PREVIEW_MIN_HEIGHT_PX + 4
  );
}

/** Single layout resolver for the modal, probe page, and automated scenario harness. */
export function resolveCameraCaptureModalLayout(
  input: CameraCaptureModalLayoutInput,
): CameraCaptureModalLayoutResult {
  const pageContentWidth =
    input.pageContentWidth > 0
      ? input.pageContentWidth
      : input.viewport.width > 0
        ? input.viewport.width
        : 0;
  const viewport =
    input.viewport.height > 0
      ? input.viewport
      : { ...input.viewport, height: input.viewport.height };
  const inputsReady = isCameraCaptureModalLayoutInputReady({
    pageContentWidth,
    viewport,
  });
  const wideTouchKiosk =
    (input.touchCoarse || input.touchHoverNone) && input.viewport.width > 1280;
  const deviceKind = readCameraCaptureDeviceKind({
    floorTablet: input.floorTablet,
    wideTouchKiosk,
    touchCoarse: input.touchCoarse,
    touchHoverNone: input.touchHoverNone,
  });
  const fillViewportPreview = shouldFillCameraCaptureViewport({
    viewportWidth: viewport.width > 0 ? viewport.width : pageContentWidth,
    floorTablet: input.floorTablet,
    wideTouchKiosk,
    deviceKind,
  });
  const layoutAspect = cameraCaptureLayoutAspectRatio({
    deviceKind,
    streamAspectRatio: input.previewAspect,
  });

  const filledLayout = fillViewportPreview && viewport.height > 0 && pageContentWidth > 0
    ? computeFilledCameraPreviewLayout({
        pageContentWidth,
        viewportHeight: viewport.height,
        aspectRatio: layoutAspect,
        deviceKind,
        hasHint: input.hasHint,
      })
    : null;
  const previewContainerLayout = fillViewportPreview && viewport.height > 0 && pageContentWidth > 0
    ? computeCameraPreviewContainerLayout({
        pageContentWidth,
        viewportHeight: viewport.height,
        aspectRatio: layoutAspect,
        deviceKind,
        hasHint: input.hasHint,
      })
    : null;

  const previewWidth =
    previewContainerLayout?.expectedWidth
    ?? filledLayout?.previewWidth
    ?? CAMERA_PREVIEW_MIN_WIDTH_PX;
  const previewHeight =
    previewContainerLayout?.expectedHeight
    ?? filledLayout?.previewHeight
    ?? CAMERA_PREVIEW_MIN_HEIGHT_PX;

  const stuckAtMinimum = isCameraCaptureLayoutStuckAtMinimum({
    pageContentWidth,
    viewportHeight: viewport.height,
    previewWidth,
    previewHeight,
  });

  return {
    inputsReady,
    fillViewportPreview,
    deviceKind,
    layoutAspect,
    filledLayout,
    previewContainerLayout,
    previewWidth,
    previewHeight,
    cardWidth: filledLayout?.cardWidth ?? pageContentWidth,
    cardMaxHeight: "calc(100dvh - 2rem)",
    previewStyle: {
      width: `${String(previewWidth)}px`,
      height: `${String(previewHeight)}px`,
      minWidth: `${String(previewWidth)}px`,
      minHeight: `${String(previewHeight)}px`,
      maxWidth: "100%",
    },
    stuckAtMinimum,
  };
}
